/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { DeleteSystemCategory } from '../models/delete-system-category';
import { SystemOptionsReturnCodes } from '../sysque-system-category-options/sysque-system-category-options.component';
import { Observable, take } from 'rxjs';
import {
  IDeleteSystemCategoryFacade,
  DELETE_SYSTEM_CATEGORY_FACADE_TOKEN,
} from '../../../../data-access/src/lib/domain/facades-models/delete-system-category-facade-interface';
import { SysqueElectricalState } from '../models/sysque-electrical-state';
import { GlobalConstants } from '../constants/global-constants';
import { CategoryOptionsReturnCodes } from '../models/category-options-return-codes';
import { ActivatedRoute } from '@angular/router';
import { AcrylicSurfaceService } from '../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from '../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-delete-system-category',
  templateUrl: './delete-system-category.component.html',
  styleUrls: ['./delete-system-category.component.scss'],
  standalone: false
})
export class DeleteSystemCategoryComponent {
  @Output() close = new EventEmitter();
  public data: DeleteSystemCategory = {
    msgSystemCategorymName: '',
  };
  public componentTitle = '';
  public result: Observable<SystemOptionsReturnCodes>;
  public systemState: SysqueElectricalState;
  public windowType = '';

  public alertMessage: string;
  public alertStatus: AlertStatus = AlertStatus.Warning;
  public alertName: string;
  public alertHeight = 290;
  public nonAlertHeight = 225;
  public contentHeight = '90px';
  public showClose = false;
  public isAlertVisible = false;
  public systemName = '';

  public get alert(): IAlert {
    return {
      message: this.alertMessage,
      type: this.alertStatus,
      isDismissable: this.showClose,
    };
  }

  constructor(
    @Inject(DELETE_SYSTEM_CATEGORY_FACADE_TOKEN) private deleteSystemCategoryFacade: IDeleteSystemCategoryFacade,
    private route: ActivatedRoute,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      if (data.isSystemOperation) {
        this.componentTitle = GlobalConstants.DELETE_SYSTEM_NAME_DIALOG;
      } else if (!data.isSystemOperation) {
        this.componentTitle = GlobalConstants.DELETE_CATEGORY_NAME_DIALOG;
      }
    });
    this.windowType = this.componentTitle.includes(GlobalConstants.System)
      ? GlobalConstants.System
      : GlobalConstants.Category;
    this.systemState = this.deleteSystemCategoryFacade.getSystemState();
    this.data.msgSystemCategorymName =
      this.windowType === GlobalConstants.System
        ? this.systemState?.selectedSystem
        : this.systemState?.selectedCategory;
    this.systemName = this.systemState?.selectedSystem;
  }

  public closeWindow(): void {
    this.deleteSystemCategoryFacade.closeWindow();
    this.close.emit();
    if (localStorage.getItem(GlobalConstants.ACRYLIC_SURFACE)) {
      this.acrylicSurfaceService.hide();
      localStorage.removeItem(GlobalConstants.ACRYLIC_SURFACE);
    }
  }

  public showAlertMessage(alertMessage: string): void {
    this.showClose = false;
    this.alertStatus = AlertStatus.Warning;
    this.alertName = 'Failure';
    this.alertMessage = alertMessage;
    this.contentHeight = '160px';
    this.deleteSystemCategoryFacade.windowResize(this.alertHeight);
    this.isAlertVisible = true;

    setTimeout(() => {
      this.isAlertVisible = false;
      this.contentHeight = '90px';
      this.deleteSystemCategoryFacade.windowResize(this.nonAlertHeight);
    }, 3000);
  }

  public onDismissClick(): void {
    this.deleteSystemCategoryFacade.windowResize(this.nonAlertHeight);
    this.contentHeight = '90px';
  }

  public deleteSystemCategory(): void {
    if (this.windowType === GlobalConstants.System) {
      this.deleteSystem();
    } else {
      this.deleteCategory();
    }
  }

  public deleteSystem(): void {
    this.deleteSystemCategoryFacade
      .removeSystem(GlobalConstants.DeleteSystemRequestName, this.data.msgSystemCategorymName)
      .pipe(take(1))
      .subscribe((result) => {
        this.deleteSystemCategoryFacade.updateSystemInfo(result.systems, null);
        localStorage.removeItem(GlobalConstants.SYSTEM_DELETED_SUCCESS);
        localStorage.setItem(GlobalConstants.SYSTEM_DELETED_SUCCESS, 'true');
        this.closeWindow();
      });
  }

  public deleteCategory(): void {
    this.deleteSystemCategoryFacade
      .deleteCategory(GlobalConstants.DeleteCategoryRequestName, this.data.msgSystemCategorymName, this.systemName)
      .pipe(take(1))
      .subscribe((result) => {
        switch (result.returnCode) {
          case CategoryOptionsReturnCodes.Success:
            this.deleteSystemCategoryFacade.updateSystemInfo(null, result.categories);
            localStorage.removeItem(GlobalConstants.CATEGORY_DELETED_SUCCESS);
            localStorage.setItem(GlobalConstants.CATEGORY_DELETED_SUCCESS, 'true');
            this.closeWindow();
            break;
          case CategoryOptionsReturnCodes.CustomContent:
            this.showAlertMessage(
              GlobalConstants.CustomContentDelete +
                this.data.msgSystemCategorymName +
                '... ' +
                GlobalConstants.CustomContentTabRequired
            );
            break;
          case CategoryOptionsReturnCodes.Error:
            this.showAlertMessage(GlobalConstants.ErrorCategoryOptions);
            break;
        }
      });
  }
}
